import { ValidationError } from 'yup';

import { Language } from '@breathelife/types';
import { AutocompleteOption } from '@breathelife/ui-components';
import { AcceptingDefaultValueFieldBlueprint, FieldBlueprint, FieldTypes } from '@zinnia/form-engine-sdk';

import { AddressAutocompleteFieldSetters } from '../Creators/useDataByField/useAddressAutocompleteFieldData';
import { AgreeFieldSetters } from '../Creators/useDataByField/useAgreeFieldData';
import { ButtonFieldSetters } from '../Creators/useDataByField/useButtonFieldData';
import { DropdownFieldSetters } from '../Creators/useDataByField/useDropdownFieldData';
import { InformationFieldSetters } from '../Creators/useDataByField/useInformationFieldData';
import { NumberFieldSetters } from '../Creators/useDataByField/useNumberFieldData';
import { RadioFieldSetters } from '../Creators/useDataByField/useRadioFieldData';
import { SignatureFieldSetters } from '../Creators/useDataByField/useSignatureFieldData';

export interface LanguageDependentSetter {
  (language: Language, value: string): void;
}

export type FieldDataHookReturnValue<TData, TSetters> = {
  data: TData;
  setters: TSetters;
  clearData: () => void;
};

export type FieldDataHookReturnValueWithValidationOptions<TData, TSetters> = FieldDataHookReturnValue<
  TData,
  TSetters
> & {
  validationOptions: AutocompleteOption[];
};

export type FieldDataValidateAs<TFieldType extends FieldBlueprint> = Pick<TFieldType, 'validateAs'>;
export type FieldSettersValidateAs<TData extends FieldDataValidateAs<FieldBlueprint>> = {
  setValidateAs: React.Dispatch<React.SetStateAction<TData['validateAs']>>;
};
export type FieldDataDefaultValue<TFieldType extends AcceptingDefaultValueFieldBlueprint> = Pick<
  TFieldType,
  'defaultValue'
>;
export type FieldSettersDefaultValue<TData extends FieldDataDefaultValue<AcceptingDefaultValueFieldBlueprint>> = {
  setDefaultValue: React.Dispatch<React.SetStateAction<TData['defaultValue']>>;
};

export type FieldDataFromBlueprint<TBlueprint extends FieldBlueprint, TUnion extends keyof TBlueprint> = Pick<
  TBlueprint,
  TUnion
>;

export type FieldDataFromBlueprintWithValidateAs<
  TBlueprint extends FieldBlueprint,
  TUnion extends keyof TBlueprint,
> = FieldDataValidateAs<TBlueprint> & FieldDataFromBlueprint<TBlueprint, TUnion>;

export function useCastBlueprintToFieldType<TBlueprintCast>(
  type: FieldTypes,
  initialData?: Partial<FieldBlueprint>,
): TBlueprintCast | undefined {
  return initialData?.fieldType === type ? (initialData as TBlueprintCast) : undefined;
}

export type FieldSetters =
  | InformationFieldSetters
  | NumberFieldSetters
  | ButtonFieldSetters
  | AgreeFieldSetters
  | DropdownFieldSetters
  | AddressAutocompleteFieldSetters
  | SignatureFieldSetters
  | RadioFieldSetters;

export type FieldDataSetterProps<TBlueprint extends FieldBlueprint, TSetters extends FieldSetters> = {
  initialData?: Partial<FieldBlueprint>;
  dataLabelOptions: AutocompleteOption[];
  setData: (data: TBlueprint) => void;
  setSetters: (setters: TSetters) => void;
  setValidationOptions: (options: AutocompleteOption[]) => void;
  setIsValidBlueprint: (isValidBlueprint: boolean) => void;
  setDefaultValueValidationError: (error: ValidationError | undefined) => void;
};
